import "./index.styles.scss";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ROUTE_PATHS } from "../../../constants/app.constants";
import { postData } from "../../../Utils/commonApi";
import { useAuth } from "../../context/Auth";
import { CallToast } from "../../PriceCalculator/modals/CustomToast";
import { Row } from "react-bootstrap";
import NavigationTabs from "../../distributor-settings/network-access/navigation-tabs";
import ContentLoader from "react-content-loader";
import ReportListSection from "./report-list-section";
import ProductSection from "./product-section";

const TabEnum = {
  Reports: "Reports",
  Products: "Products",
}

const SecStackReportPage = ({ isDistributorView = false } = {}) => {
  const divRef = useRef();
  const SearchInput = useRef();
  const hasCalledAPI = useRef(false);
  const latestRequestId = useRef(0);
  const searchTimeout = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { authTokens } = useAuth();
  const [pageData, setPageData] = useState({});
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState(1);
  const [pageNumber, setPageNumber] = useState(0);
  const [filters, setFilters] = useState([/*{ Id: 0, FilterName: "None" }*/])
  const [searchString, setSearchString] = useState('');
  const [searchInput, setSearchInput] = useState(false);
  const [activeTab, setActiveTab] = useState(TabEnum.Reports);

  useEffect(() => {
    getMSPReportsList();
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const onReportClick = () => {
    navigate(ROUTE_PATHS.subscriptionPortalPages.secStackReportReview);
  }

  const onReportViewClick = (mspId, reportId) => {
    navigate(ROUTE_PATHS.subscriptionPortalPages.secStackReportReview, {
      state: {
        forView: true,
        mspId: mspId,
        passedReportId: reportId
      }
    });
  }

  const handleInputChange = (event) => {
    const { value } = event.target;
    setSearchString(value);
    
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      getMSPReportsList({ filtersList: [{ MSPSecurityStackFilterEnum_Enum: 'Company', FilterText_List: [value] }] });
    }, 500);
  };

  const handleScroll = useCallback(() => {
    if (pageData?.SecurityStackReport_List?.length < pageData?.TotalMSP) {
      const div = divRef.current;
      const atBottom = div.scrollTop + div.clientHeight + 1 >= div.scrollHeight;

      if (atBottom && !hasCalledAPI.current) {
        hasCalledAPI.current = true;
        getMSPReportsList({ number: pageNumber });
      } else if (!atBottom) {
        hasCalledAPI.current = false;
      }
    }
  }, [pageNumber, pageData]);

  function getMSPReportsList({ passedSortOrder, number = 0, filtersList, showLoader = true } = {}) {
    const requestId = ++latestRequestId.current;
    if (number === 0) {
      if (showLoader) {
        setLoading(true);
      }
      setPageNumber(0);
    }

    postData(authTokens, "GetSecurityStackReport", {
      IsArchived: false,
      MSPNumber: number,
      MSPSecurityStackFilter_List: JSON.stringify(filtersList ?? filters ?? []),
      accessKey: JSON.parse(localStorage.getItem("user_MessageObj")),
      AccountSort: passedSortOrder ?? sortOrder ?? 1,
      AccountSortOrder: passedSortOrder ?? sortOrder ?? 1,
      pageSize: 20
    })
      .then((response) => {
        if (requestId !== latestRequestId.current) return;
        if (response?.mr?.Success) {
          if (number === 0) {
            setPageData(response);
          } else {
            let prevData = pageData;
            const existingList = pageData?.SecurityStackReport_List || [];
            const incomingList = response?.SecurityStackReport_List || [];

            const mergedList = incomingList.reduce((acc, curr) => {
              const exists = existingList.find(item => item?.MSPId === curr?.MSPId);
              if (!exists) {
                acc.push(curr);
              }
              return acc;
            }, [...existingList]);

            prevData.SecurityStackReport_List = mergedList;
            setPageData(prevData);
          }
        }
        setLoading(false);
        setPageNumber((prev) => prev + 20);
      })
      .catch((err) => {
        if (requestId !== latestRequestId.current) return;
        CallToast("Something went wrong", false, false, "v2style");
        setLoading(false);
        console.log(err);
      });
  }

  const removeReportFromList = (reportId) => {
    setPageData(prev => ({ 
      ...prev, 
      SecurityStackReport_List: prev?.SecurityStackReport_List?.map((msp) => {
        return {
          ...msp,
          MSPSecurityStack_List: msp?.MSPSecurityStack_List?.filter((report) => report?.ObjectId !== reportId)
        }
      })
    }));
  }

  const tabsConfig = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    const variant = queryParams.get("variant");
    const isV2Variant = variant === "v2";

    return [
      {
        key: TabEnum.Reports,
        label: TabEnum.Reports,
        component: ReportListSection,
        active: activeTab === TabEnum.Reports,
        onClick: () => setActiveTab(TabEnum.Reports)
      },
      {
        key: TabEnum.Products,
        label: TabEnum.Products,
        component: ProductSection,
        active: activeTab === TabEnum.Products,
        disabled: !isV2Variant,
        onClick: () => setActiveTab(TabEnum.Products)
      }
    ];
  }, [activeTab, location.search]);

  const CurrentTabComponent = useMemo(() => tabsConfig.find((tab) => tab.key === activeTab)?.component, [activeTab]);

  return (
    <div className="sec-stack-report-page transparent-scroller w-100" style={{ overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', height: !loading && '100%' }}
      ref={divRef} onScroll={() => { handleScroll(); }}
    >
      {!isDistributorView && (
        <div className="navs-container">
          <NavigationTabs tabs={tabsConfig} />
        </div>
      )}
      <CurrentTabComponent
        searchString={searchString}
        setSearchInput={setSearchInput}
        handleInputChange={handleInputChange}
        SearchInput={SearchInput}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        getMSPReportsList={getMSPReportsList}
        onReportClick={onReportClick}
        loading={loading}
        pageData={pageData}
        onReportViewClick={onReportViewClick}
        removeReportFromList={removeReportFromList}
      />
    </div>
  )
}

export default SecStackReportPage;


export const SecurityStackReportLoader = (props) => {
  return (
    <Row style={{ paddingTop: "2%" }}>
      <ContentLoader
        speed={2}
        width={'100%'}
        height={750}
        backgroundColor="#F6F8FB"
        foregroundColor="#ecebeb"
        {...props}
      >
        <rect x="2%" y="25" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="80" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="135" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="190" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="245" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="300" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="355" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="410" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="465" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="520" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="575" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="630" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="685" rx="12" ry="12" width="96%" height="50" />
      </ContentLoader>
    </Row>
  );
};